// leadsRole — route guard for /admin/leads: a platform admin OR a sales agent.
//
// aldoo-ra's requirePermission(["role:admin", "role:sales-agent"]) would ask the API for both at
// once, and the permissions service answers true only when the user holds every one of them; this
// asks for each in turn and lets in whoever holds one. The API enforces the same (requireAny).
import React, { useEffect, useState } from "react"
import { usePageContext } from "vike-react/usePageContext"
import Value from "aldoo-ra/Value"
import { api } from "aldoo-ra/API"
import AdminSignIn from "aldoo-ra/Admin/Signin"
import Typography from "aldoo-ra/Typography"

export const LEADS_ROLES = ["role:admin", "role:sales-agent"]

const permissionsAPI = api("permissions")
const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
// what was answered for the signed-in user this session: a page change does not ask again
let granted = null

/** One role: true / false, or null when the check itself failed (rate limited, offline) — retried a few times. */
async function holds(userId, role) {
  for (let attempt = 0; attempt < 4; attempt++) {
    const res = await permissionsAPI({ has_permission: { user: userId, permission: [role] } }).catch(() => null)
    if (res && !res.error) return res.result === true
    await pause(800 * 2 ** attempt)
  }
  return null
}

/** true / false, or null when it could not be checked. */
export async function holdsLeadsRole(userId) {
  if (!userId) return false
  if (granted?.userId === userId) return granted.value
  let unknown = false
  for (const role of LEADS_ROLES) {
    const answer = await holds(userId, role)
    if (answer === true) {
      granted = { userId, value: true }
      return true
    }
    if (answer === null) unknown = true
  }
  if (unknown) return null
  granted = { userId, value: false }
  return false
}

const Unchecked = () => (
  <div className="min-h-screen bg-admin_canvas flex items-center justify-center p-8">
    <div className="max-w-md bg-admin_paper rounded-2xl shadow p-8 text-center">
      <Typography variant="admin_h3">Could not check your access</Typography>
      <Typography variant="admin_body1_dim" className="mt-2">The server is busy right now. Try again in a moment.</Typography>
      <button className="inline-block mt-6 text-admin_primary hover:underline" onClick={() => window.location.reload()}>Try again</button>
    </div>
  </div>
)

const Denied = () => (
  <div className="min-h-screen bg-admin_canvas flex items-center justify-center p-8">
    <div className="max-w-md bg-admin_paper rounded-2xl shadow p-8 text-center">
      <Typography variant="admin_h3">No access to Leads</Typography>
      <Typography variant="admin_body1_dim" className="mt-2">
        Your account is signed in but is not a sales agent. Ask a platform admin to add you to the sales agents.
      </Typography>
      <a href="/admin" className="inline-block mt-6 text-admin_primary hover:underline">Back to admin</a>
    </div>
  </div>
)

export const leadsRole = (Component) =>
  function LeadsRoleWrapper(props) {
    const pageContext = usePageContext()
    const [auth] = Value("user-credentials")
    const [state, setState] = useState({ loading: true, granted: false, signedIn: false })

    useEffect(() => {
      if (typeof window === "undefined" || auth === undefined) return
      if (!auth || !auth._id) {
        setState({ loading: false, granted: false, signedIn: false })
        return
      }
      let alive = true
      holdsLeadsRole(auth._id).then((answer) => {
        if (!alive) return
        setState({ loading: false, granted: answer === true, unchecked: answer === null, signedIn: true })
      })
      return () => {
        alive = false
      }
    }, [auth])

    if (typeof window === "undefined") return <div style={{ visibility: "hidden" }} />
    if (state.loading) return <div className="p-8 text-admin_text_secondary">Loading...</div>
    if (!state.signedIn) return <AdminSignIn {...props} redirect={pageContext.urlPathname} />
    if (state.unchecked) return <Unchecked />
    if (!state.granted) return <Denied />
    return <Component {...props} />
  }
